// useContractHealth — fetches the four tracking surfaces (deliverables,
// milestones, insurance, variations) the contract Details summary
// strip aggregates from, then runs computeContractHealth() against
// today's Brisbane date. Reuses useAllDeliverablesForSummary so the
// existing deliverable summary-strip cache is shared. Returns a stable
// shape suitable for the ContractHealthSummary component, plus
// has-records flags so the strip can render "no X tracked yet" empty
// states correctly. A failed underlying query surfaces as `error`
// (isPending false) so the consumer can render an unavailable state
// instead of a permanently-pending strip.

import { useMemo } from "react";
import { useAllDeliverablesForSummary } from "./useTracking";
import { useFlatTrackingList } from "./useFlatTrackingList";
import { getBrisbaneToday } from "../utils/getBrisbaneToday";
import {
  computeContractHealth,
  type ContractHealth,
  type ContractTrackingSnapshot,
} from "../lib/contract-overview/contractHealth";
import type { Milestone, Insurance, Variation } from "../types";

const EMPTY: ContractHealth = {
  overdueDeliverables: { count: 0 },
  overdueMilestones: { count: 0 },
  insuranceExpiring: { count: 0 },
  pendingVariations: { count: 0 },
};

interface UseContractHealthResult {
  health: ContractHealth;
  hasRecords: {
    deliverables: boolean;
    milestones: boolean;
    insurance: boolean;
    variations: boolean;
  };
  isPending: boolean;
  /** First failure among the underlying tracking queries. A failed query
   *  must surface as an error, never as a permanently-pending gauge. */
  error: Error | null;
}

const PENDING_RESULT: UseContractHealthResult = {
  health: EMPTY,
  hasRecords: {
    deliverables: false,
    milestones: false,
    insurance: false,
    variations: false,
  },
  isPending: true,
  error: null,
};

export function useContractHealth(
  contractId: string,
): UseContractHealthResult {
  const deliverablesQ = useAllDeliverablesForSummary(contractId);
  const milestonesQ = useFlatTrackingList<Milestone>(contractId, "milestone");
  const insuranceQ = useFlatTrackingList<Insurance>(contractId, "insurance");
  const variationsQ = useFlatTrackingList<Variation>(contractId, "variation");

  const isPending =
    deliverablesQ.isPending ||
    milestonesQ.isPending ||
    insuranceQ.isPending ||
    variationsQ.isPending;

  // useAllDeliverablesForSummary types its error as unknown — normalise so
  // the result shape always carries Error | null.
  const rawError =
    deliverablesQ.error ??
    milestonesQ.error ??
    insuranceQ.error ??
    variationsQ.error ??
    null;
  const error = useMemo<Error | null>(
    () =>
      rawError == null
        ? null
        : rawError instanceof Error
          ? rawError
          : new Error(String(rawError)),
    [rawError],
  );

  return useMemo<UseContractHealthResult>(() => {
    if (error) {
      return { ...PENDING_RESULT, isPending: false, error };
    }
    if (
      isPending ||
      !milestonesQ.data ||
      !insuranceQ.data ||
      !variationsQ.data
    ) {
      return PENDING_RESULT;
    }

    // Reminder window is a fixed 30-day floor: the same floor is applied
    // in deriveInsuranceStatus, so no per-org override is needed here.
    const snapshot: ContractTrackingSnapshot = {
      today: getBrisbaneToday(),
      insuranceReminderDays: 30,
      deliverables: deliverablesQ.data.map((d) => ({
        id: d.id,
        name: d.name,
        status: d.status,
        dueDate: d.dueDate,
        archived: !!d.archivedAt,
        submittedAt: d.submittedAt ?? null,
      })),
      milestones: milestonesQ.data.map((m) => ({
        id: m.id,
        name: m.name,
        isDelivered: !!(m.deliveredAt || m.acceptedAt || m.paidAt),
        plannedDate: m.plannedDate,
        archived: !!m.archivedAt,
      })),
      insurance: insuranceQ.data.map((i) => ({
        id: i.id,
        name: i.name,
        expiryDate: i.expiryDate,
        archived: !!i.archivedAt,
      })),
      variations: variationsQ.data.map((v) => ({
        id: v.id,
        name: v.name,
        status: v.status,
        createdAt: v.createdAt,
        archived: !!v.archivedAt,
      })),
    };

    return {
      health: computeContractHealth(snapshot),
      hasRecords: {
        deliverables: snapshot.deliverables.length > 0,
        milestones: snapshot.milestones.length > 0,
        insurance: snapshot.insurance.length > 0,
        variations: snapshot.variations.length > 0,
      },
      isPending: false,
      error: null,
    };
  }, [
    isPending,
    error,
    deliverablesQ.data,
    milestonesQ.data,
    insuranceQ.data,
    variationsQ.data,
  ]);
}
